import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { LabelValue, LabelValueSource } from 'graphql/types';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { useGetDefaultBillingRate } from 'modules/timesheet-labels/hooks/useGetDefaultBillingRate';
import { useFeatureFlag } from 'modules/common/hooks';
import { useIsSuggestion } from 'modules/common/hooks/useIsSuggestion';
import { DependantLabels } from '../schema';
import {
  determineLabelAcceptedBy,
  usePCPTAutoUpdateHandlers
} from '../../hooks';

export const useProjectOnSelect = (
  onSelect: (value: LabelValue | null) => void,
  dependentLabels: DependantLabels,
  onClose: () => void
): ((value?: WbsLabelValueSearchResult | null) => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const {
    client: autoUpdateClient,
    task: autoUpdateTask,
    program: autoUpdateProgram
  } = usePCPTAutoUpdateHandlers({
    dependentLabels
  });

  const { updateDefaultBillingRate } = useGetDefaultBillingRate({
    dependentLabels: dependentLabels
  });

  const isSuggestion = useIsSuggestion(dependentLabels.project.label);
  const onProjectSelect = useCallback(
    async (value?: WbsLabelValueSearchResult | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.displayText
      };
      onSelect(value ? transformedValue : null);

      const { client, project, task, billing, program } = dependentLabels;
      const labelValue = isSuggestion
        ? project?.label?.suggestedValues?.[0]
        : project?.label?.value;
      const isProjectDifferent = labelValue?.idValue !== value?.id;
      const valueSourceOnReset = isSmartSuggestionEnabled
        ? LabelValueSource.Suggestion
        : LabelValueSource.User;

      if (isProjectDifferent) {
        setValue(
          `${timesheetLabelsFormField}.${task.index}.label`,
          {
            ...task.label,
            value: null,
            ...(isTimeSignalFederatedSuggestionsEnabled
              ? {
                  suggestedValues: null
                }
              : {}),
            valueSource: valueSourceOnReset,
            acceptedBy: determineLabelAcceptedBy(
              valueSourceOnReset,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );
        if (billing && billing.label) {
          setValue(
            `${timesheetLabelsFormField}.${billing.index}.label`,
            {
              ...billing.label,
              value: null,
              ...(isTimeSignalFederatedSuggestionsEnabled
                ? {
                    suggestedValues: null
                  }
                : {}),
              valueSource: valueSourceOnReset,
              acceptedBy: determineLabelAcceptedBy(
                valueSourceOnReset,
                null,
                isSmartSuggestionEnabled
              )
            },
            { shouldValidate: true, shouldDirty: true }
          );
        }
        if (value) {
          const clientValue = client?.label?.value;
          const programValue = program?.label?.value;
          if (program && !programValue?.idValue && !programValue?.stringValue) {
            const updatedProgram = await autoUpdateProgram?.({
              project: value?.id
            });
            await autoUpdateTask({
              program: updatedProgram?.id,
              project: value?.id
            });
            if (value?.id && billing && billing.label) {
              updateDefaultBillingRate({ projectUri: value.id });
            }
          } else if (
            client &&
            !clientValue?.idValue &&
            !clientValue?.stringValue
          ) {
            const updatedClient = await autoUpdateClient({
              project: value?.id
            });
            await autoUpdateTask({
              client: updatedClient?.id,
              project: value?.id
            });
            if (value?.id && billing && billing.label) {
              updateDefaultBillingRate({ projectUri: value.id });
            }
          } else {
            await autoUpdateTask({
              client: client?.label.value?.idValue,
              project: value?.id
            });
            if (value?.id && billing && billing.label) {
              updateDefaultBillingRate({ projectUri: value.id });
            }
          }
        }
      }

      onClose();
    },
    [
      onSelect,
      dependentLabels,
      isSuggestion,
      isSmartSuggestionEnabled,
      onClose,
      setValue,
      timesheetLabelsFormField,
      isTimeSignalFederatedSuggestionsEnabled,
      autoUpdateProgram,
      autoUpdateTask,
      updateDefaultBillingRate,
      autoUpdateClient
    ]
  );

  return onProjectSelect;
};

export default useProjectOnSelect;
